/**
 * Insight → Connected apps: the applications clients may connect to their
 * accounts by signing in to Connect (docs/44_CONNECTED_APPS.md), as a register
 * like every other — searched, filtered and sorted by the server. A row opens
 * the application's own page, where it is approved, suspended, edited and its
 * secrets rotated.
 *
 * Registering one shows its client secret once and leaves it **waiting for
 * approval** — the server refuses a pending application at the consent page,
 * the token endpoint and on every token, exactly as one nobody registered.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, CalendarDays, Link2, PlugZap, Plus } from 'lucide-react';
import {
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatNumber,
  type ServerColumn,
} from '@connect/web-shared';

import { AppDialog, SecretsDialog } from './connected-app-dialogs';
import {
  APPS,
  STATUS_LABELS,
  STATUS_TONES,
  appKeys,
  type ConnectedAppRow,
  type ScopeDef,
  type Secrets,
} from './connected-apps';

export function ConnectedAppsPage() {
  const navigate = useNavigate();
  const [registering, setRegistering] = useState(false);
  const [shown, setShown] = useState<{ name: string; secrets: Secrets } | null>(null);
  const scopes = useQuery({
    queryKey: appKeys.scopes,
    queryFn: () => api.get<ScopeDef[]>(`${APPS}/scopes`),
    staleTime: 10 * 60_000,
  });
  const fetchRows = useMemo(() => createServerFetcher<ConnectedAppRow>(APPS), []);
  const getFacets = useMemo(() => createFacetsFetcher(APPS), []);
  const labels = useMemo(
    () => Object.fromEntries((scopes.data ?? []).map((s) => [s.name, s.label])),
    [scopes.data],
  );

  const columns = useMemo<ServerColumn<ConnectedAppRow>[]>(
    () => [
      {
        field: 'name',
        header: 'Application',
        icon: <PlugZap className="size-3" />,
        filter: 'text',
        minWidth: 220,
        render: (row) => (
          <span className="flex items-center gap-1.5 font-medium text-heading">
            {row.name}
            {row.verified ? <BadgeCheck className="size-3.5 text-accent" aria-label="Verified" /> : null}
          </span>
        ),
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
        width: 170,
        render: (row) => <StatusChip value={row.status} tones={STATUS_TONES} labels={STATUS_LABELS} />,
      },
      {
        field: 'client_id',
        header: 'Client ID',
        filter: 'text',
        width: 270,
        render: (row) => <span className="font-mono text-xs">{row.client_id}</span>,
      },
      {
        field: 'connections',
        header: 'Connections',
        icon: <Link2 className="size-3" />,
        numeric: true,
        width: 125,
        // Counted in the statement, so it is not in the sort whitelist.
        sortable: false,
        format: (value) => formatNumber(value as number),
      },
      {
        field: 'approved_at',
        header: 'Approved',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 130,
        format: (value) => (value ? formatDate(value as string) : '—'),
      },
      {
        field: 'homepage_url',
        header: 'Homepage',
        filter: 'text',
        minWidth: 200,
        hiddenByDefault: true,
      },
      { field: 'verified', header: 'Verified', width: 100, hiddenByDefault: true, format: (v) => (v ? 'Yes' : 'No') },
      { field: 'listed', header: 'Listed', width: 90, hiddenByDefault: true, format: (v) => (v ? 'Yes' : 'No') },
      {
        field: 'created_at',
        header: 'Registered',
        filter: 'date',
        width: 130,
        hiddenByDefault: true,
        format: (value) => formatDate(value as string),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Connected apps"
        description="Applications clients may connect to their accounts by signing in to Connect. Only an approved application can be connected."
        actions={
          <Can permission="messaging.connected_app.create">
            <Button size="sm" onClick={() => setRegistering(true)}>
              <Plus className="size-3.5" />
              Register an application
            </Button>
          </Can>
        }
      />

      <ServerTable<ConnectedAppRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={appKeys.list}
        stateKey="messaging.connected-apps"
        searchPlaceholder="Search by name, client ID or homepage"
        viewField="name"
        onView={(row) => navigate(`/messaging/connected-apps/${row.public_id}`)}
        rowDetailFields={(row) => [
          { label: 'What it does', value: row.description || '—' },
          { label: 'Homepage', value: row.homepage_url },
          { label: 'May ask for', value: row.scopes.map((name) => labels[name] ?? name).join(', ') },
          { label: 'Webhook address', value: row.webhook_url ?? '—' },
          { label: 'Listed', value: row.listed ? 'Yes' : 'No' },
        ]}
        emptyTitle="No applications yet"
        emptyDescription="Register an application to let clients connect it to their accounts. It can be connected once you approve it."
        emptyAction={
          <Can permission="messaging.connected_app.create">
            <Button size="sm" onClick={() => setRegistering(true)}>
              <Plus className="size-3.5" />
              Register an application
            </Button>
          </Can>
        }
      />

      {registering ? (
        <AppDialog
          app={null}
          scopes={scopes.data ?? []}
          onClose={() => setRegistering(false)}
          onSecrets={(name, secrets) => setShown({ name, secrets })}
        />
      ) : null}
      {shown ? <SecretsDialog name={shown.name} secrets={shown.secrets} onClose={() => setShown(null)} /> : null}
    </div>
  );
}
